// @ts-nocheck
import { getLegacyBridge } from "./state";
import { formatTranslation, LOCALE_CHANGE_EVENT, translate } from "./i18n";

const THEME_STORAGE_KEY = "codex-image-theme-preference";
const THEME_OPTIONS = new Set(["system", "light", "dark"]);
const SIDEBAR_WIDTH_STORAGE_KEY = "codex-image-sidebar-width";
const SIDEBAR_MIN_WIDTH = 280;
const SIDEBAR_MAX_WIDTH = 520;
const SIDEBAR_DEFAULT_WIDTH = 347;
const MOBILE_WORKSPACE_STORAGE_KEY = "codex-image-mobile-workspace";
const MOBILE_WORKSPACES = new Set(["compose", "preview", "history", "library"]);
const MOBILE_WORKSPACE_HISTORY_KEY = "__agentraMobileWorkspace";
const MOBILE_EXIT_BOUNDARY_HISTORY_KEY = "__agentraMobileExitBoundary";

const bridge = getLegacyBridge();
const state = bridge.state;
const els = bridge.els;

let shellUiInitialized = false;
let shellUiEventsBound = false;
let previewPanelHeightFrameId = null;
let sidebarResizeFrameId = null;
let sidebarResizePendingWidth = null;
let mobileWorkspacePopstateActive = false;
let mobileExitBoundaryArmed = false;
let mobileExitConfirmOpen = false;
let mobileExitConfirmed = false;

function legacyMethod(name, ...args) {
  const method = getLegacyBridge().methods[name];
  if (typeof method !== "function") {
    throw new Error("Legacy method " + name + " is not initialized");
  }
  return method(...args);
}

function formatTaskStatus(task) { return legacyMethod("formatTaskStatus", task); }
function closePromptPopover() { legacyMethod("closePromptPopover"); }
function closePromptSnippetPopover() { legacyMethod("closePromptSnippetPopover"); }
function closeArchiveModal() { legacyMethod("closeArchiveModal"); }
function closeGallery() { legacyMethod("closeGallery"); }
function closeImageEditor() { legacyMethod("closeImageEditor"); }
function closeConfirmPopover() { legacyMethod("closeConfirmPopover"); }
function closeCompressionPopover() { legacyMethod("closeCompressionPopover"); }
function closeGalleryEditPopover() { legacyMethod("closeGalleryEditPopover"); }
function closeSystemSettingsModal() { legacyMethod("closeSystemSettingsModal"); }
function revokeUploadPreviewUrls(sources) { legacyMethod("revokeUploadPreviewUrls", sources); }
function finishBatchMarqueeSelection() { legacyMethod("finishBatchMarqueeSelection"); }
function setPromptText(value) { legacyMethod("setPromptText", value); }
function setMode(mode) { legacyMethod("setMode", mode); }
function updateSizeFromPreset() { legacyMethod("updateSizeFromPreset"); }
function updatePromptCount() { legacyMethod("updatePromptCount"); }
function updateQuantity() { legacyMethod("updateQuantity"); }
function updateCompression() { legacyMethod("updateCompression"); }
function renderImageStrip() { legacyMethod("renderImageStrip"); }
function renderTasks() { legacyMethod("renderTasks"); }
function renderPreview() { legacyMethod("renderPreview"); }
function updateRequestPreview() { legacyMethod("updateRequestPreview"); }
function i18nText(key, fallback) {
  const value = translate(key);
  return value === key ? fallback : value;
}

function handleShellLocaleChange() {
  if (!els.statusText) return;
  const current = String(els.statusText.textContent || "").trim();
  const waitingLabels = [translate("status.waiting", "zh-CN"), translate("status.waiting", "en")];
  if (waitingLabels.includes(current)) {
    setStatus(translate("status.waiting"), "");
  }
}

function bindShellUiEvents() {
  if (shellUiEventsBound) return;
  shellUiEventsBound = true;
  els.themeSwitcher?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-theme-option]");
    if (!button) return;
    applyThemePreference(button.dataset.themeOption || "system");
  });
  state.themeSystemQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
  state.themeSystemQuery?.addEventListener?.("change", handleThemeSystemChange);
  document.addEventListener(LOCALE_CHANGE_EVENT, handleShellLocaleChange);
  if (els.copyJsonButton) {
    els.copyJsonButton.addEventListener("click", copyJson);
  }
  els.newTaskButton?.addEventListener("click", resetForm);
  els.sidebarResizeHandle?.addEventListener("pointerdown", startSidebarResize);
  els.sidebarResizeHandle?.addEventListener("keydown", handleSidebarResizeKeydown);
  els.sidebarResizeHandle?.addEventListener("dblclick", resetSidebarWidth);
  document.querySelector("#mobileWorkspaceNav")?.addEventListener("click", handleMobileWorkspaceNavClick);
  els.mobileExitCancelButton?.addEventListener("click", cancelMobileExit);
  els.mobileExitConfirmButton?.addEventListener("click", confirmMobileExit);
  els.mobileExitConfirm?.addEventListener("click", (event) => {
    if (event.target === els.mobileExitConfirm) cancelMobileExit();
  });
  window.addEventListener("resize", handleMobileWorkspaceResize);
  window.addEventListener("popstate", handleMobileWorkspacePopState);
  syncSidebarResizeHandleAria();
  restoreMobileWorkspace();
}

function isMobileWorkspaceLayout() {
  return window.matchMedia("(max-width: 1024px)").matches;
}

function normalizeMobileWorkspace(value) {
  return MOBILE_WORKSPACES.has(value) ? value : "compose";
}

function currentMobileWorkspace() {
  return normalizeMobileWorkspace(document.body.dataset.mobileWorkspace || state.mobileWorkspace || "compose");
}

function setMobileWorkspace(workspace, { persist = true, focus = false, history = true } = {}) {
  const nextWorkspace = normalizeMobileWorkspace(workspace);
  const previousWorkspace = currentMobileWorkspace();
  state.mobileWorkspace = nextWorkspace;
  document.body.dataset.mobileWorkspace = nextWorkspace;
  document.querySelectorAll("[data-mobile-workspace-tab]").forEach((button) => {
    const active = button.dataset.mobileWorkspaceTab === nextWorkspace;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", active ? "true" : "false");
  });
  if (persist) {
    try {
      localStorage.setItem(MOBILE_WORKSPACE_STORAGE_KEY, nextWorkspace);
    } catch {
      // Browser storage may be unavailable in restricted contexts.
    }
  }
  if (history && isMobileWorkspaceLayout() && !mobileWorkspacePopstateActive) {
    pushMobileWorkspaceHistory(nextWorkspace, previousWorkspace);
  }
  schedulePreviewPanelHeightSync();
  if (focus && isMobileWorkspaceLayout()) {
    focusMobileWorkspace(nextWorkspace);
  }
}

function restoreMobileWorkspace() {
  let saved = "compose";
  try {
    saved = localStorage.getItem(MOBILE_WORKSPACE_STORAGE_KEY) || "compose";
  } catch {
    saved = "compose";
  }
  const nextWorkspace = normalizeMobileWorkspace(saved);
  if (isMobileWorkspaceLayout() && nextWorkspace !== "compose") {
    setMobileWorkspace("compose", { persist: false, history: false });
    resetMobileExitBoundary("compose");
    setMobileWorkspace(nextWorkspace, { persist: false, history: false });
    pushMobileWorkspaceHistory(nextWorkspace, "compose");
    return;
  }
  setMobileWorkspace(nextWorkspace, { persist: false, history: false });
  if (isMobileWorkspaceLayout()) {
    resetMobileExitBoundary(nextWorkspace);
  }
}

function handleMobileWorkspaceNavClick(event) {
  const button = event.target.closest("[data-mobile-workspace-tab]");
  if (!button) return;
  setMobileWorkspace(button.dataset.mobileWorkspaceTab || "compose", { focus: true });
}

function handleMobileWorkspaceResize() {
  if (!isMobileWorkspaceLayout()) {
    mobileExitBoundaryArmed = false;
    schedulePreviewPanelHeightSync();
    return;
  }
  setMobileWorkspace(currentMobileWorkspace(), { persist: false, history: false });
  resetMobileExitBoundary(currentMobileWorkspace());
}

function mobileWorkspaceHistoryState(workspace, extras = {}) {
  return {
    ...(window.history.state && typeof window.history.state === "object" ? window.history.state : {}),
    [MOBILE_WORKSPACE_HISTORY_KEY]: workspace,
    [MOBILE_EXIT_BOUNDARY_HISTORY_KEY]: false,
    ...extras,
  };
}

function historyMobileWorkspace() {
  return normalizeMobileWorkspace(window.history.state?.[MOBILE_WORKSPACE_HISTORY_KEY] || "compose");
}

function replaceMobileWorkspaceHistory(workspace) {
  try {
    window.history.replaceState(mobileWorkspaceHistoryState(workspace), "", window.location.href);
  } catch {
    // Some embedded browsers restrict history writes.
  }
}

function resetMobileExitBoundary(workspace) {
  if (!isMobileWorkspaceLayout()) {
    mobileExitBoundaryArmed = false;
    replaceMobileWorkspaceHistory(workspace);
    return;
  }
  try {
    if (mobileExitBoundaryArmed) {
      window.history.replaceState(mobileWorkspaceHistoryState(workspace), "", window.location.href);
      return;
    }
    window.history.replaceState(mobileWorkspaceHistoryState(workspace, { [MOBILE_EXIT_BOUNDARY_HISTORY_KEY]: true }), "", window.location.href);
    window.history.pushState(mobileWorkspaceHistoryState(workspace, { [MOBILE_EXIT_BOUNDARY_HISTORY_KEY]: false }), "", window.location.href);
    mobileExitBoundaryArmed = true;
  } catch {
    // Some embedded browsers restrict history writes.
  }
}

function pushMobileWorkspaceHistory(workspace, previousWorkspace = currentMobileWorkspace()) {
  if (workspace === previousWorkspace || workspace === historyMobileWorkspace()) return;
  try {
    window.history.pushState(mobileWorkspaceHistoryState(workspace), "", window.location.href);
  } catch {
    // Some embedded browsers restrict history writes.
  }
}

function closeTopMobileLayer() {
  if (mobileExitConfirmOpen) {
    hideMobileExitConfirm();
    return true;
  }
  if (document.body.classList.contains("lightbox-open")) {
    window.closeLightbox?.();
    return true;
  }
  const closeButton = document.querySelector("[data-history-lightbox-close]");
  const historyLightbox = closeButton?.closest?.(".history-lightbox");
  if (historyLightbox instanceof HTMLElement && !historyLightbox.hidden) {
    (closeButton as HTMLElement).click();
    return true;
  }
  if (els.accountModal && !els.accountModal.classList.contains("hidden")) {
    els.accountModal.classList.add("hidden");
    els.accountModal.setAttribute("aria-hidden", "true");
    return true;
  }
  if (els.dailyCreditWelcome && !els.dailyCreditWelcome.classList.contains("hidden")) {
    els.dailyCreditWelcome.classList.add("hidden");
    els.dailyCreditWelcome.setAttribute("aria-hidden", "true");
    return true;
  }
  if (els.systemSettingsModal && !els.systemSettingsModal.classList.contains("hidden")) {
    closeSystemSettingsModal();
    return true;
  }
  if (els.imageEditorModal && !els.imageEditorModal.classList.contains("hidden")) {
    closeImageEditor();
    return true;
  }
  if (els.galleryDrawer && els.galleryDrawer.classList.contains("open")) {
    closeGallery();
    return true;
  }
  if (document.querySelector(".confirm-popover:not(.hidden)")) {
    closeConfirmPopover();
    return true;
  }
  if (document.querySelector(".prompt-popover:not(.hidden)")) {
    closePromptPopover();
    return true;
  }
  if (document.querySelector(".compression-popover:not(.hidden)")) {
    closeCompressionPopover();
    return true;
  }
  if (document.querySelector(".gallery-edit-popover:not(.hidden)")) {
    closeGalleryEditPopover();
    return true;
  }
  if (document.querySelector(".prompt-snippet-popover:not(.hidden)")) {
    closePromptSnippetPopover();
    return true;
  }
  return false;
}

function handleMobileWorkspacePopState() {
  if (!isMobileWorkspaceLayout()) return;
  if (closeTopMobileLayer()) {
    pushMobileWorkspaceHistory(currentMobileWorkspace(), "");
    return;
  }
  if (window.history.state?.[MOBILE_EXIT_BOUNDARY_HISTORY_KEY]) {
    if (mobileExitConfirmed) return;
    showMobileExitConfirm();
    return;
  }
  const nextWorkspace = historyMobileWorkspace();
  mobileWorkspacePopstateActive = true;
  try {
    setMobileWorkspace(nextWorkspace, { persist: true, focus: true, history: false });
  } finally {
    mobileWorkspacePopstateActive = false;
  }
}

function showMobileExitConfirm() {
  mobileExitConfirmOpen = true;
  els.mobileExitConfirm?.classList.remove("hidden");
  els.mobileExitConfirm?.setAttribute("aria-hidden", "false");
  window.setTimeout(() => els.mobileExitCancelButton?.focus?.(), 0);
}

function hideMobileExitConfirm() {
  mobileExitConfirmOpen = false;
  els.mobileExitConfirm?.classList.add("hidden");
  els.mobileExitConfirm?.setAttribute("aria-hidden", "true");
}

function cancelMobileExit() {
  hideMobileExitConfirm();
  pushMobileWorkspaceHistory(currentMobileWorkspace(), "");
}

function confirmMobileExit() {
  hideMobileExitConfirm();
  mobileExitConfirmed = true;
  window.history.back();
}

function focusMobileWorkspace(workspace) {
  const target = workspace === "history"
    ? els.sidebar
    : workspace === "preview"
      ? els.previewPanel
      : workspace === "library"
        ? document.querySelector(".image-panel")
        : els.controlsCol;
  if (!(target instanceof HTMLElement)) return;
  target.scrollIntoView({ block: "start", inline: "nearest" });
}

function normalizeThemePreference(value) {
  return THEME_OPTIONS.has(value) ? value : "system";
}

function resolveEffectiveTheme(preference = state.themePreference) {
  if (preference === "dark" || preference === "light") return preference;
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

function updateThemeSwitcher() {
  els.themeSwitcher?.querySelectorAll("[data-theme-option]").forEach((button) => {
    const active = button.dataset.themeOption === state.themePreference;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", active ? "true" : "false");
  });
}

function applyThemePreference(preference, { persist = true } = {}) {
  state.themePreference = normalizeThemePreference(preference);
  const effectiveTheme = resolveEffectiveTheme(state.themePreference);
  document.documentElement.dataset.theme = effectiveTheme;
  document.documentElement.dataset.themePreference = state.themePreference;
  if (persist) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, state.themePreference);
    } catch {
      // Browser storage may be unavailable in restricted contexts.
    }
  }
  updateThemeSwitcher();
}

function restoreThemePreference() {
  let saved = "system";
  try {
    saved = localStorage.getItem(THEME_STORAGE_KEY) || "system";
  } catch {
    saved = "system";
  }
  applyThemePreference(saved, { persist: false });
}

function handleThemeSystemChange() {
  if (state.themePreference === "system") {
    applyThemePreference("system", { persist: false });
  }
}

function restoreSidebarWidth() {
  try {
    const saved = Number.parseInt(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY) || "", 10);
    if (!Number.isNaN(saved)) {
      applySidebarWidth(saved, { persist: false });
    }
  } catch {
    // Browser storage may be unavailable in restricted contexts.
  }
}

function sidebarMaxWidth() {
  const viewportWidth = window.innerWidth || SIDEBAR_MAX_WIDTH;
  if (viewportWidth <= 1024) return viewportWidth;
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, viewportWidth - 760));
}

function clampSidebarWidth(value) {
  const width = Number.parseInt(value, 10);
  if (Number.isNaN(width)) return SIDEBAR_MIN_WIDTH;
  return Math.min(sidebarMaxWidth(), Math.max(SIDEBAR_MIN_WIDTH, width));
}

function sidebarWidthFromCss() {
  const widthOwner = els.sidebar || document.documentElement;
  const inlineWidth = Number.parseInt(widthOwner.style.getPropertyValue("--sidebar-width") || "", 10);
  if (!Number.isNaN(inlineWidth)) return clampSidebarWidth(inlineWidth);
  const tokenWidth = Number.parseInt(getComputedStyle(widthOwner).getPropertyValue("--sidebar-width") || "", 10);
  return Number.isNaN(tokenWidth) ? null : clampSidebarWidth(tokenWidth);
}

function currentSidebarWidth() {
  return sidebarWidthFromCss() ?? SIDEBAR_DEFAULT_WIDTH;
}

function syncSidebarResizeHandleAria(width = null) {
  const handle = els.sidebarResizeHandle;
  if (!handle) return;
  const currentWidth = width !== null ? width : currentSidebarWidth();
  handle.setAttribute("aria-valuemin", String(SIDEBAR_MIN_WIDTH));
  handle.setAttribute("aria-valuemax", String(SIDEBAR_MAX_WIDTH));
  handle.setAttribute("aria-valuenow", String(currentWidth));
}

function applySidebarWidth(width, { persist = true, syncPreviewHeight = true } = {}) {
  const nextWidth = clampSidebarWidth(width);
  (els.sidebar || document.documentElement).style.setProperty("--sidebar-width", `${nextWidth}px`);
  syncSidebarResizeHandleAria(nextWidth);
  if (persist) {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(nextWidth));
    } catch {
      // Browser storage may be unavailable in restricted contexts.
    }
  }
  if (syncPreviewHeight) {
    schedulePreviewPanelHeightSync();
  }
}

function resetSidebarWidth() {
  applySidebarWidth(SIDEBAR_DEFAULT_WIDTH);
}

function scheduleSidebarResizeWidth(width) {
  sidebarResizePendingWidth = clampSidebarWidth(width);
  if (sidebarResizeFrameId !== null) return;
  sidebarResizeFrameId = window.requestAnimationFrame(() => {
    sidebarResizeFrameId = null;
    const nextWidth = sidebarResizePendingWidth;
    sidebarResizePendingWidth = null;
    if (nextWidth === null) return;
    applySidebarWidth(nextWidth, { persist: false, syncPreviewHeight: false });
  });
}

function flushSidebarResizeWidth(width) {
  if (sidebarResizeFrameId !== null) {
    window.cancelAnimationFrame(sidebarResizeFrameId);
    sidebarResizeFrameId = null;
  }
  sidebarResizePendingWidth = null;
  applySidebarWidth(width, { persist: true, syncPreviewHeight: true });
}

function startSidebarResize(event) {
  if (!els.sidebar || event.button !== 0) return;
  event.preventDefault();
  const currentWidth = currentSidebarWidth();
  state.sidebarResize = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startWidth: currentWidth,
    lastWidth: currentWidth,
  };
  els.sidebar.classList.add("resizing");
  if (els.sidebarResizeShield) {
    els.sidebarResizeShield.hidden = false;
  }
  els.sidebarResizeHandle?.setPointerCapture?.(event.pointerId);
  window.addEventListener("pointermove", updateSidebarResize);
  window.addEventListener("pointerup", finishSidebarResize);
  window.addEventListener("pointercancel", finishSidebarResize);
}

function updateSidebarResize(event) {
  const resize = state.sidebarResize;
  if (!resize || event.pointerId !== resize.pointerId) return;
  event.preventDefault();
  resize.lastWidth = resize.startWidth + event.clientX - resize.startX;
  scheduleSidebarResizeWidth(resize.lastWidth);
}

function finishSidebarResize(event) {
  const resize = state.sidebarResize;
  if (!resize || event.pointerId !== resize.pointerId) return;
  const nextWidth = resize.lastWidth ?? resize.startWidth;
  state.sidebarResize = null;
  els.sidebar?.classList.remove("resizing");
  if (els.sidebarResizeShield) {
    els.sidebarResizeShield.hidden = true;
  }
  els.sidebarResizeHandle?.releasePointerCapture?.(event.pointerId);
  window.removeEventListener("pointermove", updateSidebarResize);
  window.removeEventListener("pointerup", finishSidebarResize);
  window.removeEventListener("pointercancel", finishSidebarResize);
  flushSidebarResizeWidth(nextWidth);
}

function handleSidebarResizeKeydown(event) {
  if (!els.sidebar) return;
  const step = event.shiftKey ? 32 : 16;
  const currentWidth = currentSidebarWidth();
  if (event.key === "ArrowLeft") {
    event.preventDefault();
    applySidebarWidth(currentWidth - step);
  } else if (event.key === "ArrowRight") {
    event.preventDefault();
    applySidebarWidth(currentWidth + step);
  } else if (event.key === "Home") {
    event.preventDefault();
    applySidebarWidth(SIDEBAR_MIN_WIDTH);
  } else if (event.key === "End") {
    event.preventDefault();
    applySidebarWidth(SIDEBAR_MAX_WIDTH);
  }
}

function setupPreviewPanelHeightSync() {
  if (!els.controlsCol || !els.previewCol || !els.previewPanel) return;
  window.addEventListener("resize", schedulePreviewPanelHeightSync);
  if (window.ResizeObserver) {
    const observer = new ResizeObserver(schedulePreviewPanelHeightSync);
    observer.observe(els.controlsCol);
    els.controlsCol.querySelectorAll(":scope > .panel").forEach((panel) => observer.observe(panel));
  }
  schedulePreviewPanelHeightSync();
}

function schedulePreviewPanelHeightSync() {
  if (state.sidebarResize) {
    return;
  }
  if (previewPanelHeightFrameId !== null) {
    window.cancelAnimationFrame(previewPanelHeightFrameId);
  }
  previewPanelHeightFrameId = window.requestAnimationFrame(() => {
    previewPanelHeightFrameId = null;
    syncPreviewPanelHeight();
  });
}

function syncPreviewPanelHeight() {
  if (state.sidebarResize) return;
  if (!els.controlsCol || !els.previewCol || !els.previewPanel) return;
  if (window.matchMedia("(max-width: 1024px)").matches) {
    els.previewCol.style.removeProperty("--controls-col-height");
    els.previewPanel.style.removeProperty("--controls-col-height");
    return;
  }
  const panels = [...els.controlsCol.querySelectorAll(":scope > .panel")];
  if (!panels.length) return;
  const firstPanelRect = panels[0].getBoundingClientRect();
  const lastPanelRect = panels[panels.length - 1].getBoundingClientRect();
  const height = Math.max(260, Math.ceil(lastPanelRect.bottom - firstPanelRect.top));
  els.previewCol.style.setProperty("--controls-col-height", `${height}px`);
  els.previewPanel.style.setProperty("--controls-col-height", `${height}px`);
}

function updateDocumentTitle() {
  const summary = state.queue.summary || {};
  const waitingCount = Number(summary.waiting_count ?? state.queue.waiting.length ?? 0);
  const runningCount = Number(summary.running_count ?? state.queue.running.length ?? 0);
  const total = waitingCount + runningCount;
  let status = "";
  if (runningCount > 0) {
    status = formatTranslation("document.generatingQueue", { total });
  } else if (waitingCount > 0) {
    status = formatTranslation("document.queuedWaiting", { count: waitingCount });
  } else {
    const selected = state.tasks.find((item) => String(item.task_id) === String(state.selectedTaskId));
    status = selected ? formatTaskStatus(selected) : "";
  }
  document.title = status ? `${status} · ${getLegacyBridge().constants.defaultDocumentTitle}` : getLegacyBridge().constants.defaultDocumentTitle;
}

function setStatus(message, type) {
  if (!els.statusText) return;
  els.statusText.textContent = message;
  els.statusText.className = `status-text ${type || ""}`;
}

function resetForm() {
  closePromptPopover();
  closePromptSnippetPopover();
  closeArchiveModal();
  closeGallery();
  closeImageEditor();
  state.selectedTaskId = null;
  state.mode = "generate";
  revokeUploadPreviewUrls(state.images);
  state.images = [];
  state.batchMode = false;
  state.batchSelectedTaskIds = [];
  state.batchSelectionAnchorTaskId = null;
  finishBatchMarqueeSelection();
  setPromptText("");
  if (els.customSizeToggle) els.customSizeToggle.checked = false;
  if (els.nInput) els.nInput.value = "1";
  if (els.resolution) els.resolution.value = "standard";
  if (els.ratio) els.ratio.value = "1:1";
  if (els.orientation) els.orientation.value = "square";
  els.size.value = "1024x1024";
  els.quality.value = "medium";
  els.outputFormat.value = "png";
  els.moderation.value = "auto";
  els.compression.value = "80";
  if (els.promptFidelity) els.promptFidelity.value = "strict";
  [els.nInput, els.resolution, els.ratio, els.orientation, els.quality, els.outputFormat, els.moderation, els.promptFidelity].forEach((sel) => {
    if (sel) sel.dispatchEvent(new Event("change"));
  });
  setMode("generate");
  updateSizeFromPreset();
  updatePromptCount();
  updateQuantity();
  updateCompression();
  renderImageStrip();
  renderTasks();
  renderPreview();
  updateRequestPreview();
  setMobileWorkspace("compose", { persist: true });
  setStatus(translate("status.waiting"), "");
}

async function copyJson() {
  if (!els.requestJson) return;
  await navigator.clipboard.writeText(els.requestJson.textContent);
  setStatus(translate("status.jsonCopied"), "ok");
}

export function initShellUiFeature() {
  if (shellUiInitialized) return;
  shellUiInitialized = true;
  Object.assign(getLegacyBridge().methods, {
    bindShellUiEvents,
    normalizeThemePreference,
    resolveEffectiveTheme,
    updateThemeSwitcher,
    applyThemePreference,
    restoreThemePreference,
    handleThemeSystemChange,
    restoreSidebarWidth,
    sidebarMaxWidth,
    clampSidebarWidth,
    applySidebarWidth,
    resetSidebarWidth,
    setMobileWorkspace,
    restoreMobileWorkspace,
    syncSidebarResizeHandleAria,
    startSidebarResize,
    updateSidebarResize,
    finishSidebarResize,
    handleSidebarResizeKeydown,
    setupPreviewPanelHeightSync,
    schedulePreviewPanelHeightSync,
    syncPreviewPanelHeight,
    updateDocumentTitle,
    setStatus,
    resetForm,
    copyJson,
  });
}
